import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Checkbox } from '../atoms/Checkbox/Checkbox';
import { Inline } from '../atoms/Inline/Inline';
import { Input } from '../atoms/Input/Input';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { useTranslation } from '../../i18n/useTranslation';
import { ApiError } from '../../lib/apiClient';
import {
  submitOptInResponse,
  type MyOptInPrompt,
  type OptInAnswers,
  type OptInField,
  type OptInResponse,
  type OptInResponseSource,
} from '../../services/optInPromptsService';
import './OptInPromptForm.css';

type Values = Record<string, boolean | string>;
type Pending = 'save' | 'decline' | null;

function initialValues(prompt: MyOptInPrompt): Values {
  const values: Values = {};
  for (const field of prompt.fields) {
    const stored = prompt.response?.answers[field.key];
    values[field.key] = field.type === 'consent' ? stored === true : typeof stored === 'string' ? stored : '';
  }
  return values;
}

/** Every consent of the prompt declined ("Nicht teilnehmen", "Widerrufen"). */
export function declinedAnswers(prompt: MyOptInPrompt): OptInAnswers {
  const answers: OptInAnswers = {};
  for (const field of prompt.fields) if (field.type === 'consent') answers[field.key] = false;
  return answers;
}

/** The text field is only offered (and only stored) together with one of its consents. */
function textVisible(field: OptInField, values: Values): boolean {
  return field.type === 'text' && field.onlyWith.some((key) => values[key] === true);
}

function errorFieldOf(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.status !== 400) return null;
  const field = (err.data as { error?: { field?: unknown } } | undefined)?.error?.field;
  return typeof field === 'string' ? field : null;
}

export interface OptInPromptFormProps {
  prompt: MyOptInPrompt;
  source: OptInResponseSource;
  onSaved: (response: OptInResponse) => void;
  /** Extra button left of the others (e.g. "Später" in the dialog). */
  secondaryAction?: ReactNode;
  /** Label of a button that saves every consent as declined ("Nicht teilnehmen", "Widerrufen"); none when unset. */
  declineLabel?: string;
}

/**
 * The fields of one opt-in prompt: a checkbox per consent (texts from
 * optInPrompts.<id>.fields.<key>.label/hint) and text fields that only appear
 * with one of their consents (the Instagram name). Validation is the server's
 * (400 with the field) and shown under the field; other failures are an Alert.
 */
export function OptInPromptForm({ prompt, source, onSaved, secondaryAction, declineLabel }: OptInPromptFormProps) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const [values, setValues] = useState<Values>(() => initialValues(prompt));
  const [pending, setPending] = useState<Pending>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const failureRef = useRef<HTMLDivElement>(null);
  const textRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const label = (key: string, part: 'label' | 'hint'): string => t(`optInPrompts.${prompt.id}.fields.${key}.${part}`);

  useEffect(() => {
    if (failed) failureRef.current?.focus();
  }, [failed]);

  useEffect(() => {
    if (fieldError) textRefs.current[fieldError]?.focus();
  }, [fieldError]);

  async function save(answers: OptInAnswers, kind: Exclude<Pending, null>): Promise<void> {
    if (pending) return;
    setPending(kind);
    setFieldError(null);
    setFailed(false);
    try {
      const response = await submitOptInResponse(prompt.id, answers, source, i18n.resolvedLanguage ?? i18n.language);
      onSaved(response);
    } catch (err: unknown) {
      const field = errorFieldOf(err);
      if (field && prompt.fields.some((f) => f.type === 'text' && f.key === field)) setFieldError(field);
      else setFailed(true);
    } finally {
      setPending(null);
    }
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const answers: OptInAnswers = {};
    for (const field of prompt.fields) {
      if (field.type === 'consent') answers[field.key] = values[field.key] === true;
      else answers[field.key] = textVisible(field, values) ? String(values[field.key] ?? '') : null;
    }
    void save(answers, 'save');
  };

  const fieldErrorText = (field: OptInField): string | undefined => {
    if (fieldError !== field.key) return undefined;
    const empty = String(values[field.key] ?? '').trim().replace(/^@/, '') === '';
    return t(`optInPrompts.${prompt.id}.fields.${field.key}.${empty ? 'required' : 'invalid'}`);
  };

  return (
    <form id={id} onSubmit={handleSubmit} noValidate className="rg-opt-in-form">
      <Stack gap="4">
        {failed && (
          <Alert tone="danger" ref={failureRef} tabIndex={-1}>
            {t('optInPrompts.common.saveError')}
          </Alert>
        )}
        <ul className="rg-opt-in-form__fields">
          {prompt.fields.map((field) => {
            if (field.type === 'consent') {
              const hintId = `${id}-${field.key}-hint`;
              return (
                <li key={field.key}>
                  <Stack gap="1">
                    <Checkbox
                      id={`${id}-${field.key}`}
                      label={label(field.key, 'label')}
                      checked={values[field.key] === true}
                      disabled={pending !== null}
                      aria-describedby={hintId}
                      onChange={(e) => setValues((v) => ({ ...v, [field.key]: e.target.checked }))}
                    />
                    <Text id={hintId} size="sm" color="secondary" className="rg-opt-in-form__hint">
                      {label(field.key, 'hint')}
                    </Text>
                  </Stack>
                </li>
              );
            }
            if (!textVisible(field, values)) return null;
            return (
              <li key={field.key}>
                <Input
                  ref={(el) => {
                    textRefs.current[field.key] = el;
                  }}
                  id={`${id}-${field.key}`}
                  label={label(field.key, 'label')}
                  helperText={label(field.key, 'hint')}
                  error={fieldErrorText(field)}
                  value={String(values[field.key] ?? '')}
                  maxLength={field.maxLength + 1}
                  autoComplete="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  required={field.requiredWhen.some((key) => values[key] === true)}
                  disabled={pending !== null}
                  onChange={(e) => setValues((v) => ({ ...v, [field.key]: e.target.value }))}
                  fullWidth
                />
              </li>
            );
          })}
        </ul>
        <Text size="sm" color="secondary">
          {t('optInPrompts.common.revokeHint')}{' '}
          <Link to="/privacy" className="rg-opt-in-form__link">{t('optInPrompts.common.privacyLink')}</Link>
        </Text>
        <Inline gap="2" justify="end" wrap>
          {secondaryAction}
          {declineLabel && (
            <Button
              variant="secondary"
              onClick={() => void save(declinedAnswers(prompt), 'decline')}
              isLoading={pending === 'decline'}
              disabled={pending === 'save'}
            >
              {declineLabel}
            </Button>
          )}
          <Button variant="primary" type="submit" isLoading={pending === 'save'} disabled={pending === 'decline'}>
            {t('optInPrompts.common.save')}
          </Button>
        </Inline>
      </Stack>
    </form>
  );
}
